/**
 * <variant-picker>
 *
 * 1. Picks the matching product variant when an option changes. It updates the ?variant= URL
 *    parameter, sets the variant id on the page's product forms, greys out unavailable values,
 *    and dispatches a bubbling `variant:change` event with { variant } so other blocks
 *    (price, gallery, stock message) can react.
 *
 * 2. Switches between linked products without a page load. Clicking a linked-product pill
 *    fetches the other product's version of this section (Section Rendering API), swaps it in,
 *    and updates the URL. Anything that fails falls back to a normal page load.
 */

// Wrapped so the script can safely be included more than once on a page.
(() => {
  if (customElements.get('variant-picker')) return;

  const SWAP_STATE_KEY = 'variantPickerSwap';

  /** @type {AbortController | undefined} */
  let swapController;

  /** Prefetched section responses, by request URL. @type {Map<string, Promise<string>>} */
  const prefetched = new Map();

  /**
   * Tells Affirm's promotional messaging to re-scan the page. A no-op without Affirm.
   * @param {number} [priceInCents] - New price to show, when it changed without a section swap.
   */
  function refreshAffirm(priceInCents) {
    if (priceInCents !== undefined) {
      for (const message of document.querySelectorAll('.affirm-as-low-as[data-amount]')) {
        message.setAttribute('data-amount', String(priceInCents));
      }
    }

    const affirm = /** @type {any} */ (window).affirm;
    affirm?.ui?.ready?.(() => affirm.ui.refresh());
  }

  /**
   * Shopify only sets window.ShopifyAnalytics.meta.product on page load, and apps such as Affirm
   * read it later. Point it at the product that is now on screen.
   * @param {Element} section - The section that was just swapped in
   */
  function syncAnalytics(section) {
    const data = section.querySelector('script[data-analytics]');
    if (!data?.textContent) return;

    const product = JSON.parse(data.textContent);
    const analytics = /** @type {any} */ (window).ShopifyAnalytics;
    const legacyMeta = /** @type {any} */ (window).meta;

    for (const meta of [analytics?.meta, legacyMeta]) {
      if (!meta) continue;
      meta.product = product;
      if (meta.page) meta.page.resourceId = product.id;
      if (meta.selectedVariantId !== undefined) meta.selectedVariantId = product.variants[0]?.id;
    }
  }

  /**
   * @param {string} url - The product URL
   * @param {string} sectionId
   * @returns {string} The Section Rendering API URL
   */
  function sectionUrl(url, sectionId) {
    const target = new URL(url, window.location.href);
    target.searchParams.set('section_id', sectionId);
    return target.toString();
  }

  /**
   * @param {string} url
   * @param {string} sectionId
   * @param {AbortSignal} [signal]
   * @returns {Promise<string>}
   */
  function fetchSection(url, sectionId, signal) {
    const requestUrl = sectionUrl(url, sectionId);
    const cached = prefetched.get(requestUrl);
    if (cached) {
      prefetched.delete(requestUrl);
      return cached;
    }

    return fetch(requestUrl, { signal }).then((response) => {
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      return response.text();
    });
  }

  /**
   * Replaces the section's contents with the other product's, and updates the page to match.
   *
   * @param {string} url - Product URL to show
   * @param {string} sectionId - The section containing the picker
   * @param {{ push?: boolean, title?: string, previousTitle?: string }} [options]
   */
  async function swapProduct(url, sectionId, { push = true, title, previousTitle } = {}) {
    const current = document.getElementById(`shopify-section-${sectionId}`);
    if (!current) {
      window.location.href = url;
      return;
    }

    swapController?.abort();
    swapController = new AbortController();
    const { signal } = swapController;

    const hadFocus = current.contains(document.activeElement);
    current.classList.add('is-swapping');
    current.setAttribute('aria-busy', 'true');

    try {
      const html = await fetchSection(url, sectionId, signal);
      const incoming = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${sectionId}`);

      // The other product doesn't use this section (a different template): do a normal load
      if (!incoming?.querySelector('variant-picker')) {
        window.location.href = url;
        return;
      }

      // Scripts parsed here stay inert, so app block scripts won't run twice
      current.replaceChildren(...document.importNode(incoming, true).childNodes);
      syncAnalytics(current);

      if (push) {
        // Mark the page we're leaving so Back can swap rather than reload
        window.history.replaceState({ ...window.history.state, [SWAP_STATE_KEY]: true }, '');
        window.history.pushState({ [SWAP_STATE_KEY]: true }, '', url);
      }

      if (title && previousTitle) document.title = document.title.replace(previousTitle, title);

      if (hadFocus) current.querySelector('a[aria-current="page"]')?.focus();

      current.dispatchEvent(new CustomEvent('product:change', { bubbles: true, detail: { url } }));
      refreshAffirm();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      window.location.href = url;
    } finally {
      current.classList.remove('is-swapping');
      current.removeAttribute('aria-busy');
    }
  }

  // Back/forward between swapped products
  window.addEventListener('popstate', (event) => {
    if (!event.state?.[SWAP_STATE_KEY]) return;

    const picker = document.querySelector('variant-picker[data-section-id]');
    const sectionId = picker instanceof HTMLElement ? picker.dataset.sectionId : undefined;

    if (sectionId) swapProduct(window.location.href, sectionId, { push: false });
    else window.location.reload();
  });

  class VariantPicker extends HTMLElement {
    /** @type {{ id: number, available: boolean, price: number, options: string[] }[]} */
    variants = [];

    #abort = new AbortController();

    connectedCallback() {
      const { signal } = this.#abort;

      this.addEventListener('click', this.#handleLinkedProductClick, { signal });
      this.addEventListener('pointerenter', this.#prefetchLinkedProduct, { capture: true, signal });
      this.addEventListener('focusin', this.#prefetchLinkedProduct, { signal });

      const data = this.querySelector('script[data-variants]');
      if (!data?.textContent) return;

      this.variants = JSON.parse(data.textContent);
      this.addEventListener('change', this.#handleChange, { signal });
      this.#updateAvailability();
    }

    disconnectedCallback() {
      this.#abort.abort();
    }

    /** @returns {string[]} The selected value for each option, by position. */
    get selectedOptions() {
      const groups = this.querySelectorAll('[data-option-index]');
      return Array.from(groups, (group) => {
        const field = group.querySelector('input:checked, select');
        return field instanceof HTMLInputElement || field instanceof HTMLSelectElement ? field.value : '';
      });
    }

    /**
     * @param {Event} event
     * @returns {HTMLAnchorElement | null}
     */
    #linkedProductFrom(event) {
      const link = event.target instanceof Element ? event.target.closest('a[data-linked-product]') : null;
      return link instanceof HTMLAnchorElement && this.contains(link) ? link : null;
    }

    /**
     * The Spurit Pre-Order app stays bound to the product it loaded with, so it throws after an in-place
     * swap. Pre-order products (either side) use a normal page load.
     * @param {HTMLAnchorElement} link
     */
    #needsPageLoad(link) {
      return this.hasAttribute('data-preorder') || link.hasAttribute('data-preorder');
    }

    /** @param {Event} event */
    #prefetchLinkedProduct = (event) => {
      const link = this.#linkedProductFrom(event);
      const sectionId = this.dataset.sectionId;
      if (!link || !sectionId || link.hasAttribute('aria-current')) return;

      if (this.#needsPageLoad(link)) return;

      const requestUrl = sectionUrl(link.href, sectionId);
      if (!prefetched.has(requestUrl)) {
        const request = fetch(requestUrl).then((response) => {
          if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
          return response.text();
        });
        request.catch(() => prefetched.delete(requestUrl));
        prefetched.set(requestUrl, request);
      }
    };

    /** @param {MouseEvent} event */
    #handleLinkedProductClick = (event) => {
      const link = this.#linkedProductFrom(event);
      const sectionId = this.dataset.sectionId;
      if (!link || !sectionId) return;

      // Leave new-tab and modified clicks to the browser
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      if (this.#needsPageLoad(link)) return;

      event.preventDefault();
      if (link.hasAttribute('aria-current')) return;

      swapProduct(link.href, sectionId, { title: link.dataset.title, previousTitle: this.dataset.productTitle });
    };

    /** @param {Event} event */
    #handleChange = (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('[data-option-index]')) return;

      const selected = this.selectedOptions;
      const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));

      this.#updateAvailability();
      this.#updateLabels();

      if (!variant) {
        this.toggleAttribute('data-unavailable-combination', true);
        return;
      }

      this.toggleAttribute('data-unavailable-combination', false);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState(window.history.state, '', url);

      // Only this product's forms: product cards on the page have their own add to cart forms
      for (const form of document.querySelectorAll('form[action*="/cart/add"]')) {
        const formProductId = form.querySelector('input[name="product-id"]')?.getAttribute('value');
        if (formProductId !== this.dataset.productId) continue;

        const input = form.querySelector('[name="id"]');
        if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement) input.value = String(variant.id);
      }

      const meta = /** @type {any} */ (window).ShopifyAnalytics?.meta;
      if (meta) meta.selectedVariantId = variant.id;

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
      refreshAffirm(variant.price);
    };

    /** Marks values that no available variant can produce, given the other selections. */
    #updateAvailability() {
      const selected = this.selectedOptions;

      this.querySelectorAll('[data-option-index]').forEach((group) => {
        if (!(group instanceof HTMLElement)) return;
        const index = Number(group.dataset.optionIndex);

        for (const field of group.querySelectorAll('input[type="radio"], option')) {
          if (!(field instanceof HTMLInputElement || field instanceof HTMLOptionElement)) continue;

          const candidate = selected.map((value, i) => (i === index ? field.value : value));
          const available = this.variants.some(
            (v) => v.available && v.options.every((value, i) => value === candidate[i])
          );
          field.closest('label')?.toggleAttribute('data-unavailable', !available);
          if (field instanceof HTMLOptionElement) field.toggleAttribute('data-unavailable', !available);
        }
      });
    }

    /** Shows the selected value next to each option name. */
    #updateLabels() {
      const selected = this.selectedOptions;

      this.querySelectorAll('[data-option-index]').forEach((group) => {
        if (!(group instanceof HTMLElement)) return;
        const output = group.querySelector('[data-selected-value]');
        if (output) output.textContent = selected[Number(group.dataset.optionIndex)] ?? '';
      });
    }
  }

  if (!customElements.get('variant-picker')) {
    customElements.define('variant-picker', VariantPicker);
  }
})();
